<template>
  <controller :goToTopButton="true">
    <div class="restore-page">
      <header class="restore-header">
        <div>
          <span class="eyebrow">Ferramentas / Banco de dados</span>
          <h3>Restauração de Base</h3>
          <p>Origem fixa em produção. Destino liberado somente para desenvolvimento ou homologação.</p>
        </div>

        <div :class="['header-status', hasRunningRestore ? 'running' : 'ready']">
          <span>{{ hasRunningRestore ? 'Em execução' : 'Disponível' }}</span>
          <strong>{{ hasRunningRestore ? statusLabel(runningRestore.status) : 'Pronto para iniciar' }}</strong>
        </div>
      </header>

      <section class="restore-console">
        <div class="route-map">
          <div class="route-node source">
            <span>Origem</span>
            <strong>Produção</strong>
          </div>

          <div class="route-arrow">→</div>

          <div class="route-node target">
            <span>Destino</span>
            <strong>{{ environmentLabel(environment) }}</strong>
          </div>
        </div>

        <div class="environment-switch" role="group" aria-label="Ambiente de destino">
          <button
            v-for="option in environmentOptions"
            :key="option.value"
            type="button"
            :class="{ active: environment === option.value }"
            :disabled="working || hasRunningRestore"
            :aria-pressed="environment === option.value"
            @click="selectEnvironment(option.value)"
          >
            <strong>{{ option.label }}</strong>
            <span>{{ option.value.toUpperCase() }}</span>
          </button>
        </div>

        <button type="button" class="restore-button" :disabled="!allowRestore" @click.prevent="confirmRestore">
          {{ working ? 'Iniciando...' : 'Restaurar base' }}
        </button>
      </section>

      <section :class="['notice', selectedEnvironmentRunning ? 'warning' : 'info']" v-if="selectedEnvironmentRunning">
        <strong>{{ environmentLabel(selectedEnvironmentRunning.environment) }}</strong>
        <span>{{ statusLabel(selectedEnvironmentRunning.status) }}</span>
      </section>

      <section class="notice error" v-if="error">
        <strong>Falha na operação</strong>
        <span>{{ error }}</span>
      </section>

      <section class="notice error" v-if="syncError">
        <strong>Falha ao sincronizar AWS</strong>
        <span>{{ syncError }}</span>
      </section>

      <section class="history-panel">
        <div class="panel-header">
          <div>
            <h4>Histórico</h4>
            <span>{{ hasRunningRestore ? 'Atualização automática a cada 10 segundos' : 'Últimas restaurações solicitadas' }}</span>
          </div>
          <button type="button" class="refresh-button" :disabled="loading" @click="getRestauracaoBase">
            {{ loading ? 'Atualizando...' : 'Atualizar' }}
          </button>
        </div>

        <div class="skeleton-list" v-if="loading && restauracoes.length === 0">
          <span v-for="index in 4" :key="index"></span>
        </div>

        <div class="empty-state" v-else-if="restauracoes.length === 0">
          <strong>Nenhuma restauração registrada</strong>
          <span>Quando uma restauração for iniciada, o andamento aparece aqui.</span>
        </div>

        <div class="restore-table-wrap" v-else>
          <table class="restore-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Ambiente</th>
                <th>Início</th>
                <th>Fim</th>
                <th>Status</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="restauracao in restauracoes" :key="restauracao.id" :class="{ failed: isFailed(restauracao) }">
                <td>
                  <strong>#{{ restauracao.id }}</strong>
                </td>
                <td>
                  <span class="environment-pill">{{ environmentLabel(restauracao.environment) }}</span>
                </td>
                <td>{{ formatDateTime(restauracao.startedAt || restauracao.createdAt) }}</td>
                <td>{{ restauracao.finishedAt ? formatDateTime(restauracao.finishedAt) : '-' }}</td>
                <td>
                  <span :class="['tag', statusClass(restauracao.status)]">{{ statusLabel(restauracao.status) }}</span>
                </td>
                <td>
                  <span class="detail error-message" v-if="restauracao.errorMessage">{{ restauracao.errorMessage }}</span>
                  <span class="detail" v-else>{{ backupLabel(restauracao.backupS3Uri) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <confirm
      :backdrop="false"
      class="confirm"
      ref="confirm"
    ></confirm>
  </controller>
</template>

<script>
import FerramentasService from '@/services/ferramentas.service'
import { formatDateTime } from '@/services/utils.service'

export default {
  data () {
    return {
      environment: 'hom',
      environmentOptions: [
        { value: 'dev', label: 'Desenvolvimento' },
        { value: 'hom', label: 'Homologação' }
      ],
      working: false,
      loading: false,
      error: null,
      syncError: null,
      restauracoes: [],
      pollTimer: null
    }
  },
  computed: {
    hasRunningRestore () {
      return this.restauracoes.some(restauracao => this.isRunning(restauracao))
    },
    runningRestore () {
      return this.restauracoes.find(restauracao => this.isRunning(restauracao)) || null
    },
    selectedEnvironmentRunning () {
      return this.restauracoes.find(restauracao => restauracao.environment === this.environment && this.isRunning(restauracao))
    },
    allowRestore () {
      return !this.working && !this.loading && !this.hasRunningRestore
    }
  },
  mounted () {
    this.getRestauracaoBase()
  },
  beforeUnmount () {
    this.stopPolling()
  },
  methods: {
    getRestauracaoBase () {
      if (!this.pollTimer) {
        this.loading = true
      }

      this.error = null

      return FerramentasService.getRestauracaoBase()
        .then(response => {
          this.restauracoes = response.data.data || []
          this.syncError = response.data.syncError || null
          this.togglePolling()
        })
        .catch(error => {
          this.error = this.errorMessage(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    confirmRestore () {
      if (!this.allowRestore) return

      this.$refs.confirm.run({
        message: `Restaurar ${this.environmentLabel(this.environment)} com backup real-time de produção? Esta ação não pode ser desfeita.`,
        confirmButton: 'Sim',
        denyButton: 'Não',
        callback: this.restore
      })
    },
    restore (state) {
      if (!state || !this.allowRestore) return

      this.working = true
      this.error = null

      return FerramentasService.restaurarBase(this.environment)
        .then(() => {
          window.toaster.emit('open', { style: 'success', message: 'Restauração iniciada com sucesso.' })
          return this.getRestauracaoBase()
        })
        .catch(error => {
          this.error = this.errorMessage(error)
          window.toaster.emit('error', { style: 'error', message: this.error })
        })
        .finally(() => {
          this.working = false
        })
    },
    togglePolling () {
      this.hasRunningRestore ? this.startPolling() : this.stopPolling()
    },
    startPolling () {
      if (this.pollTimer) return

      this.pollTimer = window.setInterval(() => {
        this.getRestauracaoBase()
      }, 10000)
    },
    stopPolling () {
      if (!this.pollTimer) return

      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    },
    isRunning (restauracao) {
      return ['pending', 'provisioning', 'running'].includes(restauracao.status)
    },
    isFailed (restauracao) {
      return restauracao.status === 'failed'
    },
    selectEnvironment (value) {
      if (this.working || this.hasRunningRestore) return

      this.environment = value
    },
    environmentLabel (value) {
      return {
        dev: 'Desenvolvimento',
        hom: 'Homologação'
      }[value] || value || '-'
    },
    statusLabel (value) {
      return {
        pending: 'Pendente',
        provisioning: 'Provisionando',
        running: 'Restaurando',
        success: 'Ok',
        failed: 'Erro',
        stopped: 'Parado'
      }[value] || value || '-'
    },
    statusClass (value) {
      return {
        pending: 'pending',
        provisioning: 'running',
        running: 'running',
        success: 'success',
        failed: 'failed',
        stopped: 'failed'
      }[value] || 'pending'
    },
    errorMessage (error) {
      return error?.response?.data?.message || 'Ops! Algo aconteceu. Não foi possível restaurar a base neste momento.'
    },
    backupLabel (backupS3Uri) {
      if (!backupS3Uri) {
        return '-'
      }

      return backupS3Uri.split('/').pop()
    },
    formatDateTime (date) {
      return date ? formatDateTime(date, true) : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
  .restore-page {
    width: min(1320px, calc(100% - 64px));
    box-sizing: border-box;
    margin: 0 auto 32px;
    display: grid;
    gap: 14px;
  }

  .restore-header,
  .restore-console,
  .history-panel {
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 14px 34px rgba(29, 50, 66, .06);
  }

  .restore-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 20px 24px;

    h3,
    p {
      margin: 0;
    }

    h3 {
      margin-top: 4px;
      color: #0a2d45;
      font-size: 24px;
      line-height: 1.15;
    }

    p {
      margin-top: 6px;
      color: #587284;
      font-size: 13px;
    }
  }

  .eyebrow {
    color: #698498;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .header-status {
    min-width: 190px;
    display: grid;
    gap: 4px;
    padding: 12px 14px;
    border: 1px solid #cfe1eb;
    border-radius: 8px;
    background: #f7fbfd;

    span {
      color: #698498;
      font-size: 11px;
      font-weight: 900;
      text-transform: uppercase;
    }

    strong {
      color: #0a2d45;
      font-size: 14px;
    }

    &.ready {
      border-color: #bcefe8;
      background: #f0fffc;
    }

    &.running {
      border-color: #f0d690;
      background: #fff9e8;
    }
  }

  .restore-console {
    display: grid;
    grid-template-columns: minmax(320px, 1fr) minmax(360px, 1fr) 190px;
    align-items: center;
    gap: 18px;
    padding: 16px 18px;
  }

  .route-map {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .route-node {
    min-width: 130px;
    padding: 12px 14px;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #f8fbfd;

    span {
      display: block;
      color: #698498;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
    }

    strong {
      display: block;
      margin-top: 4px;
      color: #0a2d45;
      font-size: 16px;
      line-height: 1.2;
    }

    &.source {
      border-color: #ffd3dc;
      background: #fff6f8;
    }

    &.target {
      border-color: #bcefe8;
      background: #f0fffc;
    }
  }

  .route-arrow {
    color: #8aa0af;
    font-size: 20px;
    font-weight: 900;
  }

  .environment-switch {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 5px;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #f7fbfd;

    button {
      min-height: 48px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      cursor: pointer;
      text-align: left;
      padding: 8px 12px;
      transition: background .15s ease, box-shadow .15s ease;

      strong,
      span {
        display: block;
      }

      strong {
        color: #0a2d45;
        font-size: 13px;
        line-height: 1.2;
      }

      span {
        margin-top: 3px;
        color: #698498;
        font-size: 10px;
        font-weight: 900;
      }

      &.active {
        background: #fff;
        box-shadow: 0 6px 18px rgba(31, 55, 74, .12);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: .68;
      }
    }
  }

  .restore-button {
    min-height: 48px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    background: #e9002d;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(233, 0, 45, .18);

    &:disabled {
      cursor: not-allowed;
      background: #b7c5cf;
      box-shadow: none;
    }
  }

  .notice {
    display: grid;
    grid-template-columns: 180px 1fr;
    gap: 14px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;

    strong {
      color: inherit;
    }

    span {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    &.info,
    &.warning {
      color: #805b00;
      background: #fff8e4;
      border: 1px solid #f4d17c;
    }

    &.error {
      color: #9b0020;
      background: #fff0f3;
      border: 1px solid #ffbcc8;
    }
  }

  .history-panel {
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    border-bottom: 1px solid #edf3f7;

    h4 {
      margin: 0;
      color: #0a2d45;
      font-size: 17px;
      line-height: 1.2;
    }

    span {
      display: block;
      margin-top: 3px;
      color: #6c8394;
      font-size: 12px;
    }
  }

  .refresh-button {
    min-width: 96px;
    min-height: 34px;
    border: 1px solid #cfe1eb;
    border-radius: 7px;
    color: #0a2d45;
    background: #fff;
    font-weight: 800;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: .7;
    }
  }

  .restore-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .restore-table {
    width: 100%;
    min-width: 980px;
    border-collapse: collapse;

    th,
    td {
      padding: 12px 18px;
      border-top: 1px solid #edf3f7;
      color: #456273;
      font-size: 12px;
      vertical-align: middle;
    }

    tr:first-child td {
      border-top: 0;
    }

    th {
      color: #7890a2;
      font-size: 11px;
      font-weight: 900;
      text-align: left;
      text-transform: uppercase;
    }

    td strong {
      color: #0a2d45;
    }

    tbody tr.failed {
      background: #fff6f8;
    }
  }

  .environment-pill {
    display: inline-flex;
    padding: 5px 8px;
    border-radius: 6px;
    color: #00697a;
    background: #e6f8fa;
    font-size: 11px;
    font-weight: 800;
  }

  .tag {
    display: inline-flex;
    border-radius: $border-radius-small;
    padding: $hmg_mini;
    font-size: $font-size-mini !important;
    text-transform: uppercase;
    font-weight: bold !important;

    &.success {
      background-color: #dcf8ef;
      color: #087451;
    }

    &.running,
    &.pending {
      background-color: #fff4d6;
      color: #805b00;
    }

    &.failed {
      background-color: #ffe1e8;
      color: #a10022;
    }
  }

  .detail {
    display: block;
    max-width: 520px;
    overflow: hidden;
    color: #6c8394;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .error-message {
    color: #a10022;
    font-weight: 700;
  }

  .empty-state,
  .skeleton-list {
    padding: 18px;
  }

  .empty-state {
    display: grid;
    gap: 4px;
    color: #7890a2;
    font-size: 13px;

    strong {
      color: #0a2d45;
      font-size: 14px;
    }
  }

  .skeleton-list {
    display: grid;
    gap: 10px;

    span {
      height: 48px;
      border-radius: 8px;
      background: linear-gradient(90deg, #edf3f7, #f8fbfd, #edf3f7);
      background-size: 200% 100%;
      animation: skeleton 1.2s ease-in-out infinite;
    }
  }

  @keyframes skeleton {
    0% {
      background-position: 200% 0;
    }

    100% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 1100px) {
    .restore-page {
      width: calc(100% - 32px);
    }

    .restore-console {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 680px) {
    .restore-page {
      width: 100%;
    }

    .restore-header,
    .restore-console,
    .panel-header {
      display: grid;
      padding: $hmg_small;
    }

    .notice {
      display: grid;
      grid-template-columns: 1fr;
    }

    .route-map {
      display: grid;
      grid-template-columns: 1fr;
    }

    .route-arrow {
      display: none;
    }
  }
</style>
